import {
  markFrontierByRegionCounts,
  type PowerBubbleChartYearData,
  type PowerLabData,
  type PowerLabId,
  type PowerRegionId,
} from "../../../../power-bubble-chart/powerBubbleChartData"

export type TailParams = {
  num: number;
  dist: "lognormal" | "pareto" | "uniform";
  param: number;
};
export type CompanyAnchors = {
  dates: number[];
  labs: Record<
    string,
    { name: string; region: "US" | "CH" | "RoW"; series: number[] }
  >;
  tail: Record<"US" | "CH" | "RoW", Record<string, TailParams>>;
  tailAnchorYears?: number[];
  unit?: string;
  source?: string;
};

const DEFAULT_TAIL_ANCHORS = [2025, 2030, 2031, 2035, 2040];

export function getAnchorYears(data: CompanyAnchors): number[] {
  const ys =
    data.tailAnchorYears && data.tailAnchorYears.length >= 2
      ? [...data.tailAnchorYears]
      : DEFAULT_TAIL_ANCHORS;
  return ys.slice().sort((a, b) => a - b);
}

export function interpTailConfig(
  data: CompanyAnchors,
  region: "US" | "CH" | "RoW",
  year: number,
): TailParams {
  const anchors = getAnchorYears(data);
  const have = (y: number) => data.tail[region]?.[String(y)];
  if (anchors.includes(year)) {
    const cur = have(year);
    if (cur) return cur;
  }
  let lo = anchors[0];
  let hi = anchors[anchors.length - 1];
  for (const y of anchors) {
    if (y <= year && have(y)) lo = y;
  }
  for (let i = anchors.length - 1; i >= 0; i--) {
    if (anchors[i] >= year && have(anchors[i])) {
      hi = anchors[i];
      break;
    }
  }
  const a =
    have(lo) ?? have(hi) ?? { num: 10, dist: "lognormal" as const, param: 1.0 };
  const b = have(hi) ?? a;
  if (lo === hi || !Number.isFinite(year - lo)) return a;
  const t = (year - lo) / (hi - lo);
  const il = (x: number, y: number) =>
    x > 0 && y > 0 ? x * (y / x) ** t : x + (y - x) * t;
  return {
    num: Math.max(0, Math.round(il(a.num, b.num))),
    dist: a.dist,
    param: +il(a.param, b.param).toFixed(3),
  };
}

export const REGION_TO_BUBBLE: Record<
  "US" | "CH" | "RoW",
  { id: PowerRegionId; name: string; shortName: string }
> = {
  US: { id: "america", name: "America", shortName: "US" },
  CH: { id: "china", name: "China", shortName: "China" },
  RoW: { id: "world", name: "Rest of World", shortName: "RoW" },
};

const FLAGSHIP_LABS_BY_REGION: Record<"US" | "CH" | "RoW", string[]> = {
  US: ["anthropic", "openai", "google-deepmind"],
  CH: ["alibaba", "bytedance", "deepseek", "tencent"],
  RoW: ["g42", "mistral", "humain", "cohere"],
};
const FLAGSHIP_MAPPING_FROM = 2030.67;

export function tailWeights(
  num: number,
  dist: string,
  param: number,
  seed: number,
): number[] {
  const n = Math.max(0, Math.min(2000, Math.round(num)));
  if (n === 0) return [];
  let w: number[];
  if (dist === "pareto") {
    w = Array.from({ length: n }, (_, i) => Math.pow(1 / (i + 1), param));
  } else if (dist === "uniform") {
    w = Array.from({ length: n }, () => 1);
  } else {
    let s = seed | 0;
    const rand = () => {
      s = (s + 0x6d2b79f5) | 0;
      let t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    w = [];
    for (let i = 0; i < n; i++) {
      const u1 = Math.max(1e-12, rand());
      const u2 = rand();
      const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
      w.push(Math.exp(param * z));
    }
  }
  w.sort((a, b) => b - a);
  const sum = w.reduce((a, b) => a + b, 0) || 1;
  return w.map((x) => x / sum);
}

export function dateIdxForHudCalendarYear(
  dates: number[],
  calendarYear: number,
): number {
  if (dates.length === 0) return 0;
  let lastInYear = -1;
  for (let i = 0; i < dates.length; i++) {
    if (Math.floor(dates[i]) === calendarYear) lastInYear = i;
  }
  if (lastInYear >= 0) return lastInYear;
  if (calendarYear < Math.floor(dates[0])) return 0;
  if (calendarYear > Math.floor(dates[dates.length - 1]))
    return dates.length - 1;
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < dates.length; i++) {
    const dist = Math.abs(dates[i] - calendarYear);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function buildYearDataFromAnchors(
  data: CompanyAnchors,
  dateIdx: number,
  regionalStocks?: Record<"US" | "CH" | "RoW", number[]> | null,
): PowerBubbleChartYearData {
  const d = data.dates[dateIdx];
  const year = Math.floor(d);

  const M_TO_RAW = 1e6;

  type RegionBuild = {
    def: (typeof REGION_TO_BUBBLE)["US"];
    region: "US" | "CH" | "RoW";
    named: PowerLabData[];
    namedTotal: number;
    regionalTotal: number;
    tailTotal: number;
    tailWeights: number[];
    cfg: TailParams;
    tailCollapsed: boolean;
  };

  const useFlagshipMapping = d >= FLAGSHIP_MAPPING_FROM;

  const builds: RegionBuild[] = (["US", "CH", "RoW"] as const).map((r) => {
    const def = REGION_TO_BUBBLE[r];
    const flagshipSet = useFlagshipMapping
      ? new Set(FLAGSHIP_LABS_BY_REGION[r])
      : new Set<string>();
    const named: PowerLabData[] = Object.entries(data.labs)
      .filter(([id, info]) => info.region === r && !flagshipSet.has(id))
      .map(([id, info]) => ({
        id: id as PowerLabId,
        name: info.name,
        regionId: def.id,
        compute: (info.series[dateIdx] ?? 0) * M_TO_RAW,
      }));
    const namedTotal = named.reduce((s, l) => s + l.compute, 0);

    let regionalTotal: number;
    if (regionalStocks && regionalStocks[r] && regionalStocks[r][dateIdx] != null) {
      regionalTotal = regionalStocks[r][dateIdx] * M_TO_RAW;
    } else {
      const ANNUAL_FB: Record<number, number> = {
        2025: 30,
        2026: 55,
        2027: 100,
        2028: 165,
        2029: 240,
        2030: 400,
        2031: 900,
        2032: 1400,
        2033: 2000,
        2034: 2700,
        2035: 3500,
        2036: 6000,
        2037: 10000,
        2038: 16000,
        2039: 23000,
        2040: 32000,
      };
      function totalAt(decYr: number): number {
        const y = Math.floor(decYr);
        const f = decYr - y;
        const a = ANNUAL_FB[y] ?? ANNUAL_FB[2040];
        const b = ANNUAL_FB[y + 1] ?? a;
        return a > 0 && b > 0 ? a * (b / a) ** f : a;
      }
      const SHARE: Record<"US" | "CH" | "RoW", number> = {
        US: 0.70,
        CH: 0.20,
        RoW: 0.10,
      };
      regionalTotal = totalAt(d) * SHARE[r] * M_TO_RAW;
    }

    if (namedTotal > regionalTotal && namedTotal > 0) {
      const k = regionalTotal / namedTotal;
      for (const l of named) l.compute *= k;
    }
    const clippedNamedTotal = named.reduce((s, l) => s + l.compute, 0);
    const tailTotal = Math.max(0, regionalTotal - clippedNamedTotal);

    const cfg = interpTailConfig(data, r, year);
    const tailCollapsed = d < 2030.67;
    let weights: number[] = [];
    if (tailTotal > 0) {
      if (tailCollapsed) {
        weights = [1];
      } else {
        // Keep tail company identities stable as the year changes.
        const seed = 31 + (r === "US" ? 0 : r === "CH" ? 100 : 200);
        const N_CAP = 1000;
        const numShow = Math.min(N_CAP, Math.max(0, Math.round(cfg.num)));
        weights =
          numShow > 0
            ? tailWeights(numShow, cfg.dist, cfg.param, seed)
            : [];
      }
    }

    return {
      def,
      region: r,
      named,
      namedTotal: clippedNamedTotal,
      regionalTotal,
      tailTotal,
      tailWeights: weights,
      cfg,
      tailCollapsed,
    };
  });

  const usBuild = builds.find((b) => b.region === "US");
  let T = 0;
  if (usBuild) {
    if (
      useFlagshipMapping &&
      usBuild.tailWeights.length >= 3 &&
      usBuild.tailTotal > 0
    ) {
      const sortedW = [...usBuild.tailWeights].sort((a, b) => b - a);
      T = sortedW[2] * usBuild.tailTotal * 0.95;
    } else {
      const usNamedSorted = [...usBuild.named]
        .map((l) => l.compute)
        .sort((a, b) => b - a);
      if (usNamedSorted.length >= 3 && usNamedSorted[2] > 0)
        T = usNamedSorted[2] * 0.95;
    }
  }
  if (T > 0) {
    for (const b of builds) {
      if (b.region === "US") continue;
      if (b.tailCollapsed) continue;
      if (b.tailWeights.length === 0 || b.tailTotal <= 0) continue;
      const w = b.tailWeights.slice();
      for (let iter = 0; iter < 8; iter++) {
        const wMax = T / b.tailTotal;
        let surplus = 0;
        for (let i = 0; i < w.length; i++) {
          if (w[i] > wMax) {
            surplus += w[i] - wMax;
            w[i] = wMax;
          }
        }
        if (surplus <= 1e-9) break;
        const slack = w.reduce((s, x) => s + Math.max(0, wMax - x), 0);
        if (slack <= 1e-9) break;
        for (let i = 0; i < w.length; i++) {
          const room = Math.max(0, wMax - w[i]);
          if (room > 0) w[i] += surplus * (room / slack);
        }
      }
      b.tailWeights = w;
    }
  }

  const regions = builds.map((b) => {
    const tailLabel =
      b.region === "US"
        ? "Rest of US"
        : b.region === "CH"
          ? "Rest of China"
          : "Rest of World";
    let tailLabs: PowerLabData[] = [];
    if (b.tailTotal > 0 && b.tailWeights.length > 0) {
      if (b.tailCollapsed) {
        tailLabs = [
          {
            id: `${b.region}-tail` as PowerLabId,
            name: tailLabel,
            regionId: b.def.id,
            compute: b.tailTotal,
          },
        ];
      } else {
        const indexed = b.tailWeights.map((w, i) => ({ w, origIdx: i }));
        indexed.sort((a, x) => x.w - a.w);
        const flagshipList = useFlagshipMapping
          ? FLAGSHIP_LABS_BY_REGION[b.region]
          : [];
        tailLabs = indexed.map((entry, rank) => {
          const flagshipId = flagshipList[rank];
          const flagshipInfo = flagshipId ? data.labs[flagshipId] : null;
          const tailRegionPrefix =
            b.region === "US" ? "US" : b.region === "CH" ? "CN" : "RoW";
          const id: PowerLabId = (flagshipId ??
            `${b.region}-tail-${entry.origIdx}`) as PowerLabId;
          const name =
            flagshipInfo?.name ??
            `${tailRegionPrefix} co. ${entry.origIdx + 1}`;
          return {
            id,
            name,
            regionId: b.def.id,
            compute: b.tailTotal * entry.w,
          };
        });
      }
    }
    return {
      id: b.def.id,
      name: b.def.name,
      shortName: b.def.shortName,
      computeStock: b.regionalTotal,
      // The sheet's per-region star counts decide which of the region's
      // holders are frontier (largest first); the collapsed "Rest of X"
      // aggregate never takes a slot. Non-frontier labs stay individual
      // border-only cells — the treemap's own prune handles slivers.
      labs: markFrontierByRegionCounts([...b.named, ...tailLabs], b.region, d),
    };
  });

  return { year, regions };
}
